import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import {
  createGroup,
  CreateGroupRequest,
  createGroupWallet,
  getGroupList,
  SearchGroupListRequest,
  updateGroup,
  UpdateGroupRequest
} from "src/network/graphql/groupService";
import { searchUser, SearchUserRequest } from "src/network/graphql/userService";
import { Users } from "../users/usersSlice";
import { immediateToast } from "izitoast-react";

export type Group = {
  group_id: string;
  name: string;
  description: string;
  group_type: string;
  group_status: string;
  display_id: string;
  owner: {
    attributes: {
      first_name: string;
      last_name: string;
    };
    email: string;
    user_id: string;
  };
};

const initialUsers: Users[] = [];

export const initialGroup: Group[] = [];

const groupSlice = createSlice({
  name: "group",
  initialState: {
    loading: false,
    error: null,
    group: {
      list: initialGroup,
      total: 0
    },
    managerListLoading: false,
    createGroupLoading: false,
    manager: {
      list: initialUsers,
      total: 0
    }
  },
  reducers: {
    fetchGroupStart: (state) => {
      state.loading = true;
    },
    fetchGroupSuccess: (state, action) => {
      state.loading = false;
      state.group.list = action.payload?.list ?? [];
      state.group.total = action.payload?.total ?? 0;
    },
    fetchGroupFail: (state, action) => {
      state.loading = false;
      state.group.list = [];
      state.group.total = 0;
      state.error = action.payload;
    },
    fetchManagerListStart: (state) => {
      state.managerListLoading = true;
    },
    fetchManagerListSuccess: (state, action) => {
      state.managerListLoading = false;
      state.manager.list = action.payload?.list ?? [];
      state.manager.total = action.payload?.total ?? 0;
    },
    fetchManagerListFail: (state, action) => {
      state.managerListLoading = false;
      state.manager.list = [];
      state.manager.total = 0;
      state.error = action.payload;
    },
    createGroupStart: (state) => {
      state.createGroupLoading = true;
    },
    createGroupSuccess: (state) => {
      state.createGroupLoading = false;
    },
    createGroupFail: (state, action) => {
      state.createGroupLoading = false;
      state.error = action.payload;
    },
    updateGroupStart: (state) => {
      state.createGroupLoading = true;
    },
    updateGroupSuccess: (state) => {
      state.createGroupLoading = false;
    },
    updateGroupFail: (state, action) => {
      state.createGroupLoading = false;
      state.error = action.payload;
    }
  }
});

const {
  fetchManagerListStart,
  fetchManagerListSuccess,
  fetchManagerListFail,
  fetchGroupFail,
  fetchGroupStart,
  fetchGroupSuccess,
  updateGroupFail,
  updateGroupStart,
  updateGroupSuccess,
  createGroupFail,
  createGroupStart,
  createGroupSuccess
} = groupSlice.actions;

export const fetchGroupListAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group?.items;

      dispatch(
        fetchGroupSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupFail(error));
    }
  };
};

export const fetchManagerListAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchManagerListStart());
    try {
      const response = await searchUser(data);

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user.user_id,
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        email: user.email,
        status: user.is_active
      }));

      dispatch(
        fetchManagerListSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
    } catch (error) {
      dispatch(fetchManagerListFail(error));
    }
  };
};

export const updateGroupAction = (data: UpdateGroupRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateGroupStart());
    try {
      await updateGroup(data);

      dispatch(updateGroupSuccess());
      immediateToast("success", {
        message: "Group Updated Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(updateGroupFail(err));
    }
  };
};

export const createGroupAction = (data: CreateGroupRequest, cb?: (_groupId: string) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createGroupStart());
    try {
      const response = await createGroup(data);

      await createGroupWallet(data?.description, data?.name, response.data.create_group.group_id);

      dispatch(createGroupSuccess());
      immediateToast("success", {
        message: "Group Successfully Created.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb(response.data.create_group.group_id);
    } catch (error) {
      dispatch(createGroupFail(error));
    }
  };
};

export const createGroupWalletAction = (
  description: string,
  name: string,
  groupId: string,
  cb?: (_walletId: string) => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createGroupStart());
    try {
      const response = await createGroupWallet(description, name, groupId);

      dispatch(createGroupSuccess());
      immediateToast("success", {
        message: "Group Wallet Successfully Created.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb(response.data.create_group_wallet.wallet_id);
    } catch (error) {
      dispatch(createGroupFail(error));
    }
  };
};

export default groupSlice.reducer;
